// BotDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Grid, Typography, Card, CardContent, CardHeader, Alert, CircularProgress, Chip, Paper, useMediaQuery, useTheme } from "@mui/material";
import { SmartToy as BotIcon, AccessTime as TimeIcon, FormatListBulleted as LogIcon } from "@mui/icons-material";
import { getBotById } from "../services/botService";
import BotControls from "../components/Bot/BotControls";
import BotLogs from "../components/Bot/BotLogs";
import useAuth from "../hooks/useAuth";

function BotDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [bot, setBot] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const { user } = useAuth();
	const [logCount, setLogCount] = useState(0);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isTablet = useMediaQuery(theme.breakpoints.down("md"));

	useEffect(() => {
		fetchBot();
		const interval = setInterval(fetchBot, 5000);
		return () => clearInterval(interval);
	}, [id]);

	const fetchBot = async () => {
		try {
			setLoading(true);
			const data = await getBotById(id);

			// Process permissions from API response
			if (data) {
				console.log("Original bot data from API:", data);

				// Try to extract permissions in various possible formats
				// This ensures we handle different backend responses
				if (data.permissions) {
					console.log("Bot has permissions data:", data.permissions);
				}

				// If user is the owner, set full permissions
				if (data.user_id === user?.id || (data.owner && data.owner === user?.username)) {
					data.can_start = true;
					data.can_stop = true;
				}

				// Check if we have permissions data inside bot response
				const startPermission =
					Array.isArray(data.permissions) && data.permissions.some((p) => p === "start_bot" || p.permission === "start_bot" || p.can_start);

				const stopPermission =
					Array.isArray(data.permissions) && data.permissions.some((p) => p === "stop_bot" || p.permission === "stop_bot" || p.can_stop);

				// Set explicit permission flags
				if (startPermission) data.can_start = true;
				if (stopPermission) data.can_stop = true;

				console.log("Processed bot data:", {
					...data,
					processed_permissions: {
						can_start: data.can_start,
						can_stop: data.can_stop,
					},
				});

				// Count logs (this is a placeholder - ideally should come from API)
				if (data.logs && Array.isArray(data.logs)) {
					setLogCount(data.logs.length);
				} else {
					// If no logs data, estimate based on uptime
					// This is just a placeholder approximation
					const uptimeHours = parseInt(data.uptime?.split("h")[0] || "0");
					setLogCount(Math.min(28, Math.max(0, Math.floor(uptimeHours / 3))));
				}
			}

			setBot(data);
			setError(null);
		} catch (error) {
			console.error("Failed to fetch bot:", error);
			setError(error.response?.data?.message || "Failed to load bot details");
		} finally {
			setLoading(false);
		}
	};

	const handleAction = () => {
		fetchBot();
	};

	const handleDelete = () => {
		navigate("/bots");
	};

	if (loading && !bot) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ mb: 3 }}>
				{error}
			</Alert>
		);
	}

	// Helper function to get status color
	const getStatusColor = (status) => {
		if (!status) return "#9e9e9e"; // Gray for unknown
		switch (status.toLowerCase()) {
			case "running":
				return "#4caf50"; // Green
			case "online":
				return "#4caf50"; // Green
			case "stopped":
				return "#9e9e9e"; // Gray
			case "error":
				return "#f44336"; // Red
			default:
				return "#9e9e9e"; // Gray for other statuses
		}
	};

	return (
		<Box>
			{bot && (
				<>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 1 }}>
						<Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" } }}>
							{bot.name}
						</Typography>
						<Chip label={bot.status} color={bot.status === "running" ? "success" : bot.status === "error" ? "error" : "default"} />
					</Box>

					<Grid container spacing={2}>
						{/* For mobile layout: Controls first, then status metrics, then logs */}
						{isMobile ? (
							<>
								{/* Controls on mobile */}
								<Grid item xs={12}>
									<Card sx={{ borderRadius: 2, mb: 2 }}>
										<CardHeader title="Controls" />
										<CardContent>
											<BotControls bot={bot} onAction={handleAction} onDelete={handleDelete} />
										</CardContent>
									</Card>
								</Grid>

								{/* Status metrics on mobile */}
								<Grid item xs={12}>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
										{/* Bot Status Card */}
										<Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
											<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
												<BotIcon sx={{ color: "#4caf50", mr: 1 }} />
												<Typography variant="subtitle1" fontWeight="medium">
													Bot Status
												</Typography>
											</Box>
											<Box sx={{ display: "flex", alignItems: "center" }}>
												<Typography variant="h5" fontWeight="bold">
													{bot.status === "running" ? "Online" : bot.status}
												</Typography>
												<Box
													sx={{
														width: 10,
														height: 10,
														borderRadius: "50%",
														bgcolor: getStatusColor(bot.status),
														ml: 1,
													}}
												/>
											</Box>
										</Paper>

										{/* Uptime and Logs in a row */}
										<Box sx={{ display: "flex", gap: 2 }}>
											{/* Uptime Card */}
											<Paper elevation={0} sx={{ p: 2, borderRadius: 2, flex: 1 }}>
												<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
													<TimeIcon sx={{ color: "#ffc107", mr: 1 }} />
													<Typography variant="subtitle2" fontWeight="medium">
														Uptime
													</Typography>
												</Box>
												<Typography variant="h5" fontWeight="bold">
													{bot.uptime || "0h 0m"}
												</Typography>
											</Paper>

											{/* Log Entries Card */}
											<Paper elevation={0} sx={{ p: 2, borderRadius: 2, flex: 1 }}>
												<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
													<LogIcon sx={{ color: "#000", mr: 1 }} />
													<Typography variant="subtitle2" fontWeight="medium">
														Logs
													</Typography>
												</Box>
												<Typography variant="h5" fontWeight="bold">
													{logCount}
												</Typography>
											</Paper>
										</Box>
									</Box>
								</Grid>

								{/* Logs on mobile */}
								<Grid item xs={12}>
									<Card sx={{ borderRadius: 2 }}>
										<CardHeader title="Live Logs" />
										<CardContent sx={{ p: 0 }}>
											<BotLogs botId={id} onLogCountChange={setLogCount} />
										</CardContent>
									</Card>
								</Grid>
							</>
						) : (
							<>
								{/* Controls for tablet and desktop */}
								<Grid item xs={12} md={4}>
									<Card sx={{ borderRadius: 2, mb: 3 }}>
										<CardHeader title="Controls" />
										<CardContent>
											<BotControls bot={bot} onAction={handleAction} onDelete={handleDelete} />
										</CardContent>
									</Card>

									{/* Status Metrics Cards */}
									<Box
										sx={{
											display: "flex",
											flexDirection: isTablet ? "column" : "row",
											gap: 2,
											mb: 3,
										}}
									>
										{/* Bot Status Card */}
										<Paper
											elevation={0}
											sx={{
												p: 2,
												flex: 1,
												borderRadius: 2,
												display: "flex",
												flexDirection: "column",
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
												<BotIcon sx={{ color: "#4caf50", mr: 1 }} />
												<Typography variant="subtitle1" fontWeight="medium">
													Bot Status
												</Typography>
											</Box>
											<Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
												<Typography variant="h5" fontWeight="bold" sx={{ mr: 1 }}>
													{bot.status === "running" ? "Online" : bot.status}
												</Typography>
												<Box
													sx={{
														width: 12,
														height: 12,
														borderRadius: "50%",
														bgcolor: getStatusColor(bot.status),
														ml: 1,
													}}
												/>
											</Box>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Status update
											</Typography>
										</Paper>

										{/* Uptime Card */}
										<Paper
											elevation={0}
											sx={{
												p: 2,
												flex: 1,
												borderRadius: 2,
												display: "flex",
												flexDirection: "column",
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
												<TimeIcon sx={{ color: "#ffc107", mr: 1 }} />
												<Typography variant="subtitle1" fontWeight="medium">
													Uptime
												</Typography>
											</Box>
											<Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
												{bot.uptime || "0h 0m"}
											</Typography>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Since last restart
											</Typography>
										</Paper>

										{/* Log Entries Card */}
										<Paper
											elevation={0}
											sx={{
												p: 2,
												flex: 1,
												borderRadius: 2,
												display: "flex",
												flexDirection: "column",
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
												<LogIcon sx={{ color: "#000", mr: 1 }} />
												<Typography variant="subtitle1" fontWeight="medium">
													Log Entries
												</Typography>
											</Box>
											<Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
												{logCount}
											</Typography>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Total log entries
											</Typography>
										</Paper>
									</Box>
								</Grid>

								{/* Logs for tablet and desktop */}
								<Grid item xs={12} md={8}>
									<Card sx={{ borderRadius: 2 }}>
										<CardHeader title="Live Logs" />
										<CardContent sx={{ p: 0 }}>
											<BotLogs botId={id} onLogCountChange={setLogCount} />
										</CardContent>
									</Card>
								</Grid>
							</>
						)}
					</Grid>
				</>
			)}
		</Box>
	);
}

export default BotDetail;
